import {inject, Injectable} from '@angular/core';
import {combineLatest, firstValueFrom, Observable} from 'rxjs';
import {UserStore} from '../stores/user.store';
import {User} from '../api/models/user';
import {map, take} from 'rxjs/operators';
import {UserGroupQuery} from './user-group.query';
import {EntityQueryService} from '../modules/akita-mimic/services/entity-query.service';
import {UserState} from '../modules/akita-mimic/models/store-states/user-state.model';
import {SortOrder} from '../modules/akita-mimic/enums/sort-order.enum';
import {HashMap} from '../modules/akita-mimic/models/hash-map.model';

@Injectable({ providedIn: 'root' })
export class UserQuery extends EntityQueryService<UserState, User> {
    public users$: Observable<Array<User>>;
    public selectedUser$: Observable<User | undefined>;
    public isLoading$: Observable<boolean>;
    private readonly activeUsers$: Observable<Array<User>>;
    private readonly contacts$: Observable<Array<User>>;
    private readonly userGroupQuery = inject(UserGroupQuery);

    public constructor() {
        const store = inject(UserStore);
        super(store, {
            sortBy: 'fullName',
            sortByOrder: SortOrder.ASC
        });

        this.users$ = this.selectAll();
        this.activeUsers$ =
            this.selectActives()
                .pipe(
                    map((users: Array<User | undefined>) => {
                            return (users.filter(u => !!u) as User[])
                                .sort(
                                    (firstUser, secondUser) => (firstUser.fullName || '').localeCompare(secondUser.fullName)
                                );
                        }
                    )
                );
        this.selectedUser$ = this.select('selected')
            .pipe(map(() => this.getSelectedUser()));
        this.isLoading$ = this.selectLoading();

        this.contacts$ =
            combineLatest([
                this.activeUsers$,
                this.userGroupQuery.userGroupMember$.pipe(map(members => members.map(member => member.userId)))
            ])
                .pipe(map(([activeUsers, members]) => {
                    return activeUsers.filter(user => !members.includes(user.id));
                }));
    }

    public getActiveUsers(): Array<User> {
        const users = Object.values(this.getValue().entities as HashMap<User>);
        const filteredUsers = users.filter(user => !!user);
        filteredUsers.sort((firstUser, secondUser) => (firstUser.fullName || '').localeCompare(secondUser.fullName));
        return filteredUsers;
    }

    public getUserById(userId: string): User | undefined {
        if (this.hasEntity(userId)) {
            return this.getEntity(userId);
        }
        return undefined;
    }

    public getSelectedUser(): User | undefined {
        const selectedId = this.getValue().selected;
        if (selectedId && this.hasEntity(selectedId)) {
            return this.getEntity(selectedId);
        }
        return undefined;
    }

    public async getContactsLength(): Promise<number> {
        const users = await firstValueFrom(this.contacts$);
        return users.length;
    }

    public async getPaginatedContacts(offset: number, limit: number): Promise<Array<User>> {
        const storeGroups = await firstValueFrom(this.contacts$.pipe(take(1)));
        let groups: Array<User> = [];
        if (storeGroups) {
            groups = storeGroups.slice(offset, offset + limit);
        }
        return groups;
    }
}
